import { isSameDay } from "date-fns";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import type { Event, ListAccountEventsItem } from "@/api/bale.zod";

import { useGetEvent, useGetProfileSuspense, useListAccountEventsSuspenseInfinite } from "@/api/bale";
import { Button } from "@/components/Button";
import { Card, CardStack, CardSubtitleHeader, CardTab, CardTabs } from "@/components/Card";
import { ContentFull, ContentLayout } from "@/components/ContentLayout";
import { FullScreenLayout } from "@/components/FullScreenLayout";
import { Missing } from "@/components/Missing";
import { TextContent } from "@/components/TextContent";
import { ViewDetector } from "@/components/ViewDetector";
import { profileDisplayName } from "@/model/profile";

type Tab = "upcoming" | "completed" | "not_ready";

function sanitizeTab(value: string | null): Tab | null {
  if (value === "upcoming" || value === "completed" || value === "not_ready") return value;
  return "upcoming";
}

export function ProfileEventsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const tab = sanitizeTab(search.get("tab"));

  const profile = useGetProfileSuspense(params.accountId!, { query: { select: (data) => data.data.profile } });
  const me = useGetProfileSuspense("@me", { query: { select: (data) => data.data.profile } });
  const accountId = profile.data.id === me.data.id ? "@me" : profile.data.id;

  return (
    <FullScreenLayout>
      <ContentLayout
        title={<Link to={`/profile/${profile.data.id}/events`}>{t("hosted-events")}</Link>}
        subtitle={profileDisplayName(profile.data)}
        wet
      >
        <ContentFull className="pt-26">
          <CardStack>
            <CardTabs>
              <CardTab active={tab === "upcoming"} onClick={() => navigate({ search: "?tab=upcoming" })}>
                {t("events-tab-upcoming")}
              </CardTab>
              <CardTab active={tab === "completed"} onClick={() => navigate({ search: "?tab=completed" })}>
                {t("events-tab-completed")}
              </CardTab>
              <CardTab active={tab === "not_ready"} onClick={() => navigate({ search: "?tab=not_ready" })}>
                {t("events-tab-not-ready")}
              </CardTab>
            </CardTabs>

            <Card>
              <CardSubtitleHeader>
                {tab === "upcoming" && t("events-tab-upcoming")}
                {tab === "completed" && t("events-tab-completed")}
                {tab === "not_ready" && t("events-tab-not-ready")}
              </CardSubtitleHeader>
              <Suspense>
                {tab === "upcoming" && <UpcomingEventsTab accountId={accountId} />}
                {tab === "completed" && <CompletedEventsTab accountId={accountId} />}
                {tab === "not_ready" && <NotReadyEventsTab accountId={accountId} />}
              </Suspense>
            </Card>
          </CardStack>
        </ContentFull>
      </ContentLayout>
    </FullScreenLayout>
  );
}

function UpcomingEventsTab({ accountId }: { accountId: string }) {
  const events = useListAccountEventsSuspenseInfinite(
    accountId,
    { event_status: ["Upcoming", "Current"] },
    {
      query: {
        select: (data) => data.pages.flatMap((page) => page.data.events),
        getNextPageParam: (data) => data.data.page_info.next_cursor,
      },
    },
  );
  return (
    <EventsList
      events={events.data}
      isFetchingMore={events.isFetchingNextPage}
      onLoadMore={events.hasNextPage ? () => events.fetchNextPage() : undefined}
    />
  );
}

function CompletedEventsTab({ accountId }: { accountId: string }) {
  const events = useListAccountEventsSuspenseInfinite(
    accountId,
    { event_status: ["Complete"] },
    {
      query: {
        select: (data) => data.pages.flatMap((page) => page.data.events),
        getNextPageParam: (data) => data.data.page_info.next_cursor,
      },
    },
  );
  return (
    <EventsList
      events={events.data}
      isFetchingMore={events.isFetchingNextPage}
      onLoadMore={events.hasNextPage ? () => events.fetchNextPage() : undefined}
    />
  );
}

function NotReadyEventsTab({ accountId }: { accountId: string }) {
  const events = useListAccountEventsSuspenseInfinite(
    accountId,
    { event_status: ["NotReady"] },
    {
      query: {
        select: (data) => data.pages.flatMap((page) => page.data.events),
        getNextPageParam: (data) => data.data.page_info.next_cursor ?? undefined,
      },
    },
  );
  return (
    <EventsList
      events={events.data}
      isFetchingMore={events.isFetchingNextPage}
      onLoadMore={events.hasNextPage ? () => events.fetchNextPage() : undefined}
    />
  );
}

function EventsList({
  events,
  isFetchingMore,
  onLoadMore,
}: {
  events: ListAccountEventsItem[];
  isFetchingMore: boolean;
  onLoadMore?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="@container flex flex-col">
      {events.map(({ event, current_attendees, pending_invites }) => (
        <EventItem key={event.id} event={event} currentAttendees={current_attendees} pendingInvites={pending_invites} />
      ))}
      {events.length === 0 && <Missing className="mx-4 mb-4">{t("no-events-found")}</Missing>}
      {onLoadMore && (
        <ViewDetector once onEnterView={onLoadMore} className="flex flex-col items-center p-4">
          <Button type="button" disabled={isFetchingMore} onClick={onLoadMore}>
            {t("load-more")}
          </Button>
        </ViewDetector>
      )}
    </div>
  );
}

function EventItem({
  event,
  currentAttendees,
  pendingInvites,
}: {
  event: Event;
  currentAttendees: number;
  pendingInvites: number;
}) {
  const { t } = useTranslation();

  const eventDetails = useGetEvent(event.id, {}, { query: { select: (data) => data.data } });

  const isNotPossiblyVisible =
    eventDetails.data &&
    eventDetails.data.event_participants.length === 1 &&
    !eventDetails.data.event_participants[0].discoverable_distance &&
    !pendingInvites;

  return (
    <Link
      to={`/event/${event.id}`}
      className="flex flex-col justify-between gap-4 border-t border-divider p-4 hover:bg-black/5 @md:flex-row"
    >
      <div className="flex max-w-[60ch] flex-col justify-between gap-1">
        <div className="truncate font-interest">{event.name || t("untitled-event")}</div>
        <TextContent className="truncate font-disinterest text-placeholder" content={event.description} />
      </div>
      <div className="flex flex-col gap-1 @md:items-end">
        <div className="mb-auto font-hint">
          {!event.start_time && <span className="text-placeholder">{t("time-unset")}</span>}
          {event.start_time && !event.end_time && t("time", { date: event.start_time })}
          {event.start_time &&
            event.end_time &&
            isSameDay(event.start_time, event.end_time) &&
            t("time-range", { startDate: event.start_time, endDate: event.end_time })}
          {event.start_time &&
            event.end_time &&
            !isSameDay(event.start_time, event.end_time) &&
            t("datetime-range", { startDate: event.start_time, endDate: event.end_time })}
        </div>
        <div className="truncate font-hint">
          {event.location || <span className="text-placeholder">{t("event-location-not-set")}</span>}
        </div>
        <div className="font-hint">
          {isNotPossiblyVisible && <span className="text-danger">{t("event-is-not-visible")}</span>}
          {!isNotPossiblyVisible &&
            !!pendingInvites &&
            t("event-attendance-with-invites", {
              current: currentAttendees,
              max: event.max_attendees,
              invited: pendingInvites,
            })}
          {!isNotPossiblyVisible &&
            pendingInvites === 0 &&
            t("event-attendance", {
              current: currentAttendees,
              max: event.max_attendees,
            })}
        </div>
      </div>
    </Link>
  );
}
